import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Gift, Building2, ArrowRight, Check, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useContent } from '../../context/ContentContext';
import api from '../../lib/api';

const donationLevels = [
  { amount: 25, label: 'Supporter', impact: 'Provides supplies for one literacy tutoring session' },
  { amount: 50, label: 'Advocate', impact: 'Sponsors a woman to attend a leadership workshop' },
  { amount: 100, label: 'Champion', impact: 'Funds a month of mentorship program resources' },
  { amount: 250, label: 'Leader', impact: 'Supports a full scholarship for Leadership Academy' },
  { amount: 500, label: 'Benefactor', impact: 'Underwrites a community wellness event' },
  { amount: 1000, label: 'Visionary', impact: 'Funds a year of programming for one initiative' },
];

const monthlyLevels = [
  { amount: 10, label: 'Sustainer', impact: 'Keeps literacy tutoring supplies stocked every month' },
  { amount: 25, label: 'Champion Circle', impact: 'Funds ongoing mentorship resources year-round' },
  { amount: 50, label: 'Legacy Builder', impact: 'Sustains leadership workshops all year long' },
];

const IMPACT_ITEMS = [
  'Leadership development programs and workshops',
  'Scholarships for women seeking education and advancement',
  'Community service projects and volunteer initiatives',
  'Literacy and education support programs',
  'Health and wellness resources for women',
  'Advocacy and equity initiatives',
];

const DonorWallSection = ({ wall }) => {
  if (!wall.length) return null;
  return (
    <section className="py-20 bg-white" data-testid="donation-wall">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
            With Gratitude
          </span>
          <h2 className="font-serif text-3xl text-[#3c4a2a] mt-3 mb-4">
            Thank You to Our Supporters
          </h2>
          <p className="text-[#3c4a2a]/70">
            We're grateful to the generous women and neighbors who make our work possible.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {wall.map((donor, i) => (
            <div
              key={`${donor.name}-${donor.date}`}
              data-testid={`wall-donor-${i}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#f1e3eb] text-[#3c4a2a]"
            >
              <Heart className="w-4 h-4 text-[#bd8cae]" />
              <span className="font-medium">{donor.name}</span>
              {donor.monthly && (
                <span className="text-xs text-[#5d6b3c] font-medium uppercase tracking-wider">Monthly Giver</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DonatePage = () => {
  const { c } = useContent();
  const [wall, setWall] = React.useState([]);

  React.useEffect(() => {
    api.get('/donations/wall').then((res) => setWall(res.data || [])).catch(() => {});
  }, []);

  return (
    <main className="pt-52">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#bd8cae]/40 to-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-[#bd8cae]/20 flex items-center justify-center mx-auto mb-8">
              <Heart className="w-10 h-10 text-[#bd8cae]" />
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mb-6">
              {c('donate.hero.title', 'Support Our Mission')}
            </h1>
            <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
              {c('donate.hero.text', 'Your generous donation helps us empower women, develop leaders, and strengthen Lincoln County. Every gift makes a difference.')}
            </p>
          </div>
        </div>
      </section>

      {/* Donation Levels */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-serif text-3xl text-[#3c4a2a] mb-4">
              {c('donate.levels.heading', 'Choose Your Impact')}
            </h2>
            <p className="text-[#3c4a2a]/70">
              {c('donate.levels.text', "Select a giving level that's right for you. All donations are tax-deductible.")}
            </p>
            <div
              className="mt-6 inline-flex items-center gap-2 bg-[#bd8cae]/20 border border-[#bd8cae]/40 rounded-full px-5 py-2.5 text-sm text-[#3c4a2a]"
              data-testid="offline-giving-note"
            >
              <Mail className="w-4 h-4 text-[#a3688f] flex-shrink-0" />
              {c('donate.offline.note', "Online giving is coming soon! To make a gift today, please contact us — we'd love to talk with you.")}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {donationLevels.map((level) => (
              <Card
                key={level.amount}
                className="border border-[#bd8cae]/30 hover:border-[#5d6b3c] hover:shadow-lg transition-all duration-300"
              >
                <CardHeader className="text-center pb-2">
                  <p className="text-sm text-[#bd8cae] uppercase tracking-wider font-medium">
                    {level.label}
                  </p>
                  <CardTitle className="font-serif text-4xl text-[#5d6b3c] mt-2">
                    ${level.amount}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-[#3c4a2a]/70 text-sm">
                    {level.impact}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 max-w-3xl mx-auto">
            <Card className="bg-[#f1e3eb]/60 border border-[#5d6b3c]/15" data-testid="how-to-give-card">
              <CardContent className="p-8">
                <h3 className="font-serif text-2xl text-[#3c4a2a] text-center mb-6">
                  {c('donate.give.heading', 'How to Give Today')}
                </h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#5d6b3c]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#5d6b3c]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#3c4a2a] mb-0.5">In Person or Another Way</p>
                      <p className="text-[#3c4a2a]/70 text-sm" data-testid="give-contact-text">
                        {c('donate.give.contact', "Questions or another way to give? Email Info@wclcnc.com or call (828) 838-8688 — we'll make it easy.")}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <Link to="/contact">
                    <Button
                      data-testid="donate-contact-btn"
                      className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-8"
                    >
                      Contact Us to Give
                      <Heart className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Monthly Giving */}
      <section id="monthly" className="py-20 bg-[#5d6b3c]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              Sustaining Support
            </span>
            <h2 className="font-serif text-3xl text-[#3c4a2a] mt-3 mb-4">
              {c('donate.monthly.heading', 'Become a Monthly Giver')}
            </h2>
            <p className="text-[#3c4a2a]/70">
              {c('donate.monthly.text', 'A recurring monthly gift provides reliable, year-round support for our programs. Cancel anytime.')}
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {monthlyLevels.map((level) => (
              <Card
                key={level.amount}
                className="border border-[#5d6b3c]/20 hover:border-[#5d6b3c] hover:shadow-lg transition-all duration-300"
              >
                <CardHeader className="text-center pb-2">
                  <p className="text-sm text-[#bd8cae] uppercase tracking-wider font-medium">
                    {level.label}
                  </p>
                  <CardTitle className="font-serif text-4xl text-[#5d6b3c] mt-2">
                    ${level.amount}
                    <span className="text-base text-[#3c4a2a]/60 font-sans">/month</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-[#3c4a2a]/70 text-sm">{level.impact}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Wall */}
      <DonorWallSection wall={wall} />

      {/* Impact Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
                Your Impact
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-6">
                {c('donate.impact.heading', 'Where Your Donation Goes')}
              </h2>
              <div className="space-y-4">
                {IMPACT_ITEMS.map((item) => (
                  <div key={item} className="flex items-start">
                    <Check className="w-5 h-5 text-[#9dad84] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-[#3c4a2a]/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={c('donate.impact.image', 'https://static.prod-images.emergentagent.com/jobs/fc3829d4-353f-4fc4-b817-cd1ab3ff1600/images/7c9d658b712c7d2d0217fdffe8c95aa111afbdb27fe9c55131aa533c40d0e2fb.jpeg')}
                  alt="Women volunteers serving the community"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#bd8cae]/40 rounded-full -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-serif text-3xl text-[#3c4a2a] mb-4">
              {c('donate.otherways.heading', 'Other Ways to Give')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <Card className="bg-[#f1e3eb] border-0">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-[#bd8cae]/50 flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-7 h-7 text-[#5d6b3c]" />
                </div>
                <h3 className="font-serif text-xl text-[#3c4a2a] mb-3">
                  {c('donate.corporate.title', 'Corporate Giving')}
                </h3>
                <p className="text-[#3c4a2a]/70 text-sm mb-4">
                  {c('donate.corporate.text', 'Partner with us through corporate sponsorships, matching gifts, or employee giving programs.')}
                </p>
                <Link to="/contact">
                  <Button variant="link" className="text-[#5d6b3c]">
                    Contact Us
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="bg-[#f1e3eb] border-0">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-[#bd8cae]/50 flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-7 h-7 text-[#5d6b3c]" />
                </div>
                <h3 className="font-serif text-xl text-[#3c4a2a] mb-3">
                  {c('donate.legacy.title', 'Legacy Giving')}
                </h3>
                <p className="text-[#3c4a2a]/70 text-sm mb-4">
                  {c('donate.legacy.text', "Include the Women's Club in your estate plans to create a lasting legacy for future generations of women.")}
                </p>
                <Link to="/contact">
                  <Button variant="link" className="text-[#5d6b3c]">
                    Learn More
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Tax Info */}
      <section className="py-12 bg-[#5d6b3c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/90">
            {c('donate.tax.text', "The Women's Club of Lincoln County is a 501(c)(3) nonprofit organization. All donations are tax-deductible to the extent allowed by law.")}
          </p>
        </div>
      </section>
    </main>
  );
};

export default DonatePage;
